import { useRef, useState } from 'react';
import {
  getAuth,
  RecaptchaVerifier,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
} from 'firebase/auth';

import { LOGIN_VIEWS } from '../policy/loginViews';
import { getSafeErrorIdentifier } from '../../utils/getSafeErrorIdentifier';
import { getPhoneHint } from '../policy/totpChallenge';
import { rememberLastUsedFactor } from '../policy/lastUsedFactor';

// Sign-in challenge leg of the SMS machinery: send the first SMS for a
// multi-factor resolver, re-send on demand and resolve the sign-in with the
// code the user types.
export default function useSmsChallenge({
  language,
  tenantID,
  phoneRef,
  verificationID,
  setVerificationID,
  setPhoneNumber,
  setIsLogin,
  setCurrentView,
  setLoginErrorKey,
  onChallengeResolved,
  setOPT,
  setCaptchaCode,
  setIsverifySMSCode,
  onSignInResolved,
}) {
  // A ref, not state: nothing renders off it, and the unmount cleanup below
  // must see the verifier that the latest mount actually created.
  const resendVerifierRef = useRef(null);
  const [finishedSMS, setFinishedSMS] = useState(false);
  // Bumped whenever the challenge screen is left, so a callback that lands
  // afterwards can tell it no longer owns the screen it would steer.
  const challengeGenerationRef = useRef(0);
  const isCurrentChallenge = generation =>
    generation === challengeGenerationRef.current;
  // Survives the unmount on purpose: an outstanding send (first or resend) still
  // owns the challenge, so a re-entry must wait instead of sending a second SMS.
  const smsInFlightRef = useRef(false);
  // Only the newest send may publish a verificationID: two can overlap on a
  // manual resend, and an older answer would rewind the challenge just received.
  const smsTicketRef = useRef(0);
  // The verifier the outstanding send is riding on, and one left behind by a
  // departure that could not destroy it yet. See resetChallengeRecaptcha.
  const pendingVerifierRef = useRef(null);
  const retiredVerifierRef = useRef(null);
  const takeSmsTicket = verifier => {
    smsInFlightRef.current = true;
    pendingVerifierRef.current = verifier;
    smsTicketRef.current += 1;
    return smsTicketRef.current;
  };
  const isLatestSms = ticket => ticket === smsTicketRef.current;
  const clearVerifier = verifier => {
    try {
      verifier?.clear();
    } catch (error) {
      console.error(
        'useSmsChallenge.clearVerifier:',
        getSafeErrorIdentifier(error),
      );
    }
  };
  const settleSms = ticket => {
    if (!isLatestSms(ticket)) return;
    smsInFlightRef.current = false;
    pendingVerifierRef.current = null;
    clearVerifier(retiredVerifierRef.current);
    retiredVerifierRef.current = null;
  };
  // Safe here and only here: the SDK's own `verifier._reset()` runs in the
  // finally inside _verifyPhoneNumber, so by the time the send settles there is
  // no sessionInfo left to strand — the hazard the retire dance above guards
  // against is a destroy mid-send. Going through clear() also means grecaptcha
  // dismantles its widget through its own API while #first-sms is still in the
  // DOM; taking that node away first is what threw reading .style.
  const releaseFirstSmsVerifier = verifier => {
    if (window.recaptchaVerifier === verifier) {
      window.recaptchaVerifier = null;
    }
    // A departure mid-send already handed this one to settleSms, which destroys
    // it as the send lands. Clearing it here too would destroy it twice.
    if (retiredVerifierRef.current === verifier) return;
    clearVerifier(verifier);
  };

  const logInValidation = async resolverAuth => {
    const auth = getAuth();
    auth.languageCode = language;
    auth.tenantId = tenantID;
    const generation = challengeGenerationRef.current;
    let recaptchaVerifier = resendVerifierRef.current;
    if (resendVerifierRef.current === null) {
      recaptchaVerifier = await new RecaptchaVerifier(
        'recaptcha-button-resend',
        {
          size: 'invisible',
          callback: function (_) {
            if (!isCurrentChallenge(generation)) return;
            setLoginErrorKey(null);
            // getPhoneHint instead of hints[0]: the phone factor is not
            // necessarily first once TOTP can be enrolled on the same account.
            const resendPhoneHint = getPhoneHint(resolverAuth);
            if (resendPhoneHint) {
              setPhoneNumber(resendPhoneHint.phoneNumber);
              const phoneInfoOptions = {
                multiFactorHint: resendPhoneHint,
                session: resolverAuth.session,
              };
              const phoneAuthProvider = new PhoneAuthProvider(auth);
              const ticket = takeSmsTicket(recaptchaVerifier);
              phoneAuthProvider
                .verifyPhoneNumber(phoneInfoOptions, recaptchaVerifier)
                .then(verificationId => {
                  settleSms(ticket);
                  if (!isLatestSms(ticket)) return;
                  // Adopted like the first send: the SMS reached the phone, so
                  // this is the live challenge even if the user stepped away.
                  setVerificationID(verificationId);
                  setFinishedSMS(true);
                  if (!isCurrentChallenge(generation)) return;
                  setIsLogin(true);
                  setCurrentView(LOGIN_VIEWS.SMS_CODE);
                })
                .catch(error => {
                  settleSms(ticket);
                  if (!isCurrentChallenge(generation)) return;
                  if (error.code === 'auth/too-many-requests') {
                    setLoginErrorKey('heimdal_app.Login.too_many_requests');
                  } else {
                    setLoginErrorKey('heimdal_app.Login.try_again_later');
                  }
                });
            } else {
              let newPhone = phoneRef.current ? phoneRef.current : '';
              const user = auth.currentUser;
              const PhoneNumberRemoveSpace = newPhone.replace(/\s+/g, '');
              if (PhoneNumberRemoveSpace.length > 0) {
                try {
                  multiFactor(user)
                    .getSession()
                    .then(function (multiFactorSession) {
                      // Left the code screen while the session was resolving:
                      // dropping out here also spares the user a stray SMS.
                      if (!isCurrentChallenge(generation)) return;
                      try {
                        const phoneInfoOptions = {
                          phoneNumber: PhoneNumberRemoveSpace,
                          session: multiFactorSession,
                        };
                        const phoneAuthProvider = new PhoneAuthProvider(auth);
                        phoneAuthProvider
                          .verifyPhoneNumber(
                            phoneInfoOptions,
                            recaptchaVerifier,
                          )
                          .then(verificationId => {
                            if (!isCurrentChallenge(generation)) return;
                            setVerificationID(verificationId);
                            setCurrentView(LOGIN_VIEWS.SMS_CODE);
                          })
                          .catch(error => {
                            if (!isCurrentChallenge(generation)) return;
                            if (error.code === 'auth/too-many-requests') {
                              setLoginErrorKey('heimdal_app.Login.too_many_requests');
                            } else {
                              setLoginErrorKey('heimdal_app.Login.try_again_later');
                            }
                          });
                      } catch (error) {
                        console.error(
                          'useSmsChallenge.logInValidation[verifyPhoneNumber]:',
                          getSafeErrorIdentifier(error),
                        );
                        setLoginErrorKey('heimdal_app.Login.try_again_later');
                      }
                    });
                } catch (error) {
                  console.error(
                    'useSmsChallenge.logInValidation[getSession]:',
                    getSafeErrorIdentifier(error),
                  );
                  setLoginErrorKey('heimdal_app.Login.try_again_later');
                }
              }
            }
          },
        },
        auth,
      );
      resendVerifierRef.current = recaptchaVerifier;
      recaptchaVerifier.render();
    }
  };

  // What the resend button presses. The verifier used to be anchored to that
  // button, so grecaptcha ran itself on the click — but the button re-renders
  // on every countdown tick, and React writing its children back wipes the DOM
  // grecaptcha injected there; restoring it afterwards is what threw reading
  // `.style`. Anchored to a container React never renders into, nothing fires
  // it on its own, so the press has to. `verify()` resolves with the token and
  // grecaptcha invokes the configured callback, which is what does the send.
  // The SDK resets the verifier in `_verifyPhoneNumber`'s finally, so the next
  // press finds it armed again.
  const triggerResend = () => {
    const verifier = resendVerifierRef.current;
    if (!verifier) return;
    Promise.resolve(verifier.verify()).catch(error => {
      console.error(
        'useSmsChallenge.triggerResend:',
        getSafeErrorIdentifier(error),
      );
      setLoginErrorKey('heimdal_app.Login.try_again_later');
    });
  };

  // Both verifiers are bound to DOM anchors owned by the code screen: kept alive
  // they would re-bind the next mount to detached nodes.
  const resetChallengeRecaptcha = () => {
    [window.recaptchaVerifier, resendVerifierRef.current].forEach(verifier => {
      if (!verifier) return;
      // Destroying one mid-send loses the id: firebase's reset in a finally throws
      // on a destroyed verifier and replaces the sessionInfo. Hand it to settleSms.
      if (verifier === pendingVerifierRef.current) {
        retiredVerifierRef.current = verifier;
        return;
      }
      clearVerifier(verifier);
    });
    window.recaptchaVerifier = null;
    resendVerifierRef.current = null;
    // Retires every callback still in flight before re-arming, so a late
    // answer cannot set finishedSMS back to true behind the new mount.
    challengeGenerationRef.current += 1;
    // Re-arms the first-sms anchor so a challenge that never got a
    // verificationID can be retried on the next entry.
    setFinishedSMS(false);
  };

  const sendFirstSMS = async resolverAuth => {
    // Re-entering the challenge (back from the method chooser) must not burn a
    // second SMS: the code from the outstanding challenge is still valid.
    if (verificationID) {
      setFinishedSMS(true);
      return;
    }
    if (smsInFlightRef.current) return;
    const generation = challengeGenerationRef.current;
    // No ticket is taken until verifyPhoneNumber is about to run, so an unmoved
    // ticket later proves no other send claimed the slot this call is taking.
    const ticketOnEntry = smsTicketRef.current;
    try {
      smsInFlightRef.current = true;
      const auth = getAuth();
      auth.languageCode = language;
      auth.tenantId = tenantID;
      // Held locally as well: resetChallengeRecaptcha nulls the global, and
      // verifyPhoneNumber rejects with auth/argument-error on a null verifier.
      const firstSmsVerifier = await new RecaptchaVerifier(
        'first-sms',
        { size: 'invisible' },
        auth,
      );
      // The user can leave while grecaptcha loads, and resetChallengeRecaptcha
      // found no verifier to retire then: only this attempt can stop itself.
      if (!isCurrentChallenge(generation)) {
        // Safe to destroy outright, unlike above: no send ever rode it, so there
        // is no sessionInfo to strand. Publishing it would leak a detached node.
        clearVerifier(firstSmsVerifier);
        // Only this attempt's own claim: once another send holds a ticket the
        // slot is legitimately busy, and freeing it would burn a second SMS.
        if (smsTicketRef.current === ticketOnEntry) {
          smsInFlightRef.current = false;
        }
        return;
      }
      window.recaptchaVerifier = firstSmsVerifier;
      const firstSmsPhoneHint = getPhoneHint(resolverAuth);
      if (firstSmsPhoneHint) {
        setPhoneNumber(firstSmsPhoneHint.phoneNumber);
        const phoneInfoOptions = {
          multiFactorHint: firstSmsPhoneHint,
          session: resolverAuth.session,
        };
        const phoneAuthProvider = new PhoneAuthProvider(auth);
        const ticket = takeSmsTicket(firstSmsVerifier);
        phoneAuthProvider
          .verifyPhoneNumber(phoneInfoOptions, firstSmsVerifier)
          .then(verificationId => {
            // Before settleSms, so the retired-verifier check can still see a
            // departure that happened while this send was in flight.
            releaseFirstSmsVerifier(firstSmsVerifier);
            settleSms(ticket);
            if (!isLatestSms(ticket)) return;
            // Recorded even for an abandoned attempt: the SMS really went out,
            // so a re-entry adopts this challenge instead of sending again.
            setVerificationID(verificationId);
            setFinishedSMS(true);
            if (!isCurrentChallenge(generation)) return;
            setIsLogin(true);
            setCurrentView(LOGIN_VIEWS.SMS_CODE);
          })
          .catch(error => {
            releaseFirstSmsVerifier(firstSmsVerifier);
            settleSms(ticket);
            if (!isCurrentChallenge(generation)) return;
            if (error.code === 'auth/too-many-requests') {
              setLoginErrorKey('heimdal_app.Login.too_many_requests');
            } else {
              setLoginErrorKey('heimdal_app.Login.try_again_later');
            }
            setFinishedSMS(true);
          });
      } else {
        // No ticket was taken on these two paths: nothing reached the wire, so
        // releasing the slot cannot free it out from under another send.
        smsInFlightRef.current = false;
        if (isCurrentChallenge(generation)) setFinishedSMS(true);
      }
    } catch (error) {
      smsInFlightRef.current = false;
      console.error('useSmsChallenge.sendFirstSMS:', getSafeErrorIdentifier(error));
      if (!isCurrentChallenge(generation)) return;
      setFinishedSMS(true);
      setLoginErrorKey('heimdal_app.Login.try_again_later');
    }
  };

  // Returns whether the code reached Firebase: a synchronous give-up leaves the
  // parent's in-flight guard to release, since no later answer will do it.
  function verifyChallengeCode(resolverAuth, captchaCode) {
    const auth = getAuth();
    auth.languageCode = language;
    auth.tenantId = tenantID;
    // A credential built with no verification session fails server-side as
    // MISSING_SESSION_INFO, instead of a recoverable client-side error.
    if (!verificationID) {
      setOPT(false);
      setLoginErrorKey('heimdal_app.Login.try_again_later');
      setIsverifySMSCode(false);
      return false;
    }
    try {
      const cred = PhoneAuthProvider.credential(verificationID, captchaCode);
      const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
      resolverAuth
        .resolveSignIn(multiFactorAssertion)
        .then(userCredential => {
          // Firebase has signed the user in and the pending credential is now
          // spent: from here only the session exchange may be retried.
          onSignInResolved();
          // Firebase's canonical phone factor id is the literal 'phone'.
          rememberLastUsedFactor('phone');
          const idToken = userCredential._tokenResponse.idToken;
          const refreshToken = userCredential._tokenResponse.refreshToken;
          const body = { idToken, refreshToken };
          onChallengeResolved(body);
        })
        .catch(error => {
          if (error.code === 'auth/invalid-verification-code') {
            setOPT(false);
            setCaptchaCode('');
            setLoginErrorKey('heimdal_app.Login.wrong_code');
          } else {
            setOPT(false);
            console.error(
              'useSmsChallenge.verifyChallengeCode:',
              getSafeErrorIdentifier(error),
            );
            setLoginErrorKey('heimdal_app.Login.try_again_later');
          }
          setIsverifySMSCode(false);
        });
      return true;
    } catch (error) {
      setOPT(false);
      console.error(
        'useSmsChallenge.verifyChallengeCode:',
        getSafeErrorIdentifier(error),
      );
      setLoginErrorKey('heimdal_app.Login.try_again_later');
      setIsverifySMSCode(false);
      return false;
    }
  }

  return {
    logInValidation,
    triggerResend,
    sendFirstSMS,
    verifyChallengeCode,
    resetChallengeRecaptcha,
    finishedSMS,
  };
}
